/**
 * Column Visibility Utility
 *
 * This utility manages visibility settings for coding dropdown columns
 * in the transactions table, coding sidesheet, and mobile views.
 *
 * VISIBILITY FLOW:
 * 1. ERP System Level - Which columns the ERP supports (Sage 300, Intacct, QBO, Flex)
 * 2. Paidol Settings Level - Which features are enabled (location, department, etc.)
 * 3. Admin Visibility Level - Which roles can see each column (codingColumnsConfig)
 * 4. User Preferences - Personal column hiding/ordering (columnPreferences on PaidolUser)
 */

import { Role, ColumnVisibilityLevel, CodingColumnsConfig, CodingColumnSettings, Paidol } from 'API';
import { AgaveSourceSystem as AgaveSourceSystemConst } from 'app/services/agave/agaveConstants';
import { hasErpCodingUI } from 'app/util/paidolAccountType';

// ─────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────

export type CodingColumnId =
  | 'glCode'
  | 'project'
  | 'costCode'
  | 'costType'
  | 'vendor'
  | 'subJob'
  | 'location'
  | 'department'
  | 'subaccount'
  | 'employee'
  // SEE-1135 — per-transaction Sage Intacct billable item. Sage-Intacct-only and
  // gated behind the paidol's billable opt-in. Optional (never a required field),
  // so it has no AGAVE_FIELD_MAP entry — mirrors how transactionType is omitted.
  | 'billableItem'
  | 'accountingDate'
  | 'jobCode'
  // SEE-974 — Vista equipment coding. `transactionType` is a derived selector
  // column (GL/Project/Equipment), not a stored coding entity, so it has no
  // AGAVE_FIELD_MAP entry. All four are gated behind the equipment flag in
  // useCodingSystemConfig.
  | 'transactionType'
  | 'equipment'
  | 'emCostCode'
  | 'emCostType';

/** SEE-974 — coding column ids that only appear when the equipment-coding flag
 *  is on. Stripped from the visible/ordered ids in useCodingSystemConfig when
 *  the flag is off, so they flow through the normal column pipeline otherwise. */
export const EQUIPMENT_CODING_COLUMN_IDS: readonly CodingColumnId[] = [
  'transactionType',
  'equipment',
  'emCostCode',
  'emCostType',
];

/** SEE-974 — coding columns that are pinned to the front of the table and cannot
 *  be drag-reordered (per the Figma). They are still configurable: visibility,
 *  custom label, and required persist like any other column. `transactionType`
 *  must always lead the coding columns. */
export const PINNED_FIRST_CODING_COLUMN_IDS: readonly CodingColumnId[] = ['transactionType'];

/** SEE-974 — the GL/Project/Equipment coding type of a transaction. */
export type CodingType = 'equipment' | 'project' | 'gl';

/** Minimal transaction shape needed to derive a coding type. */
export interface CodingTypeFields {
  agaveEquipmentId?: string | null;
  agaveEquipment?: unknown;
  agaveProject?: unknown;
  agaveGlCode?: unknown;
  // SEE-974 — the explicitly-selected, persisted coding type (UPPERCASE
  // GL/PROJECT/EQUIPMENT from the backend). When set it wins over derived coding,
  // so the displayed type matches the server type filter even on uncoded rows.
  codingType?: string | null;
}

/** SEE-974 — map a persisted codingType (UPPERCASE, as stored/indexed) to the
 *  lowercase CodingType used throughout the UI. Returns null for absent/unknown. */
export function normalizeStoredCodingType(stored: string | null | undefined): CodingType | null {
  switch ((stored ?? '').toUpperCase()) {
    case 'GL':
      return 'gl';
    case 'PROJECT':
      return 'project';
    case 'EQUIPMENT':
      return 'equipment';
    default:
      return null;
  }
}

/**
 * SEE-974 (Model B) — the type to DISPLAY for a transaction. The transaction type
 * is the user's EXPLICIT choice only: first the optimistic client override (the
 * in-session pick), then the persisted codingType from the backend. It is NOT
 * derived from the coding, so coding a value never changes the displayed type or
 * the column greying — a row stays untyped (blank "Type") until the user picks one
 * from the dropdown. SINGLE SOURCE OF TRUTH shared by the table Type cell and the
 * sidesheet type selector.
 */
export function resolveDisplayCodingType(
  fields: CodingTypeFields | null | undefined,
  override?: string | null
): CodingType | null {
  // 'none' is the in-session "user cleared the type" sentinel — forces blank even
  // if a codingType is still persisted (until the cleared null write resolves).
  if (override === 'none') return null;
  if (override === 'gl' || override === 'project' || override === 'equipment') return override;
  return normalizeStoredCodingType(fields?.codingType);
}

/** SEE-1091 — the scalar coding-id fields that indicate a transaction already
 *  carries ERP coding (Agave + Flex). Used by the bulk-coding "mixed selection"
 *  check to split a selection into coded vs uncoded. `codingType` is deliberately
 *  excluded — it's the chosen type, not an applied coding value. */
const ANY_CODING_ID_FIELDS = [
  'agaveGlCodeId',
  'agaveProjectId',
  'agaveSubJobId',
  'agaveCostCodeId',
  'agaveCostTypeId',
  'agaveVendorId',
  'agaveLocationId',
  'agaveDepartmentId',
  'agaveSubaccountId',
  'agaveEmployeeId',
  'agaveEquipmentId',
  'agaveEmCostCodeId',
  'agaveEmCostTypeId',
  'flexGlCodeId',
  'flexCodeId',
  'flexCostCodeId',
  'flexCostTypeId',
  'flexVendorId',
] as const;

/** SEE-1091 — true when the transaction has any ERP coding value applied. Drives
 *  the bulk-coding mixed-selection warning ("X are already coded"). Broad by
 *  design: any single coding value counts. Checks BOTH the scalar `*Id` field and
 *  its denormalized object (e.g. `agaveProjectId` AND `agaveProject`) — a coded
 *  row loaded from the list query can carry the nested object with the flat id
 *  null, so checking ids alone would miss it. */
export function transactionHasAnyCoding(transaction: Record<string, unknown> | null | undefined): boolean {
  if (!transaction) return false;
  return ANY_CODING_ID_FIELDS.some(
    (idField) => Boolean(transaction[idField]) || Boolean(transaction[idField.replace(/Id$/, '')])
  );
}

/** SEE-974 — the coding columns that belong to each transaction type. Single
 *  source of truth for (a) the sidesheet form's per-type fields and (b) hiding
 *  the other types' coding columns from the table when the action-bar type filter
 *  is set. Vendor + transactionType are common to every type (not listed here). */
export const CODING_COLUMNS_BY_TYPE: Record<CodingType, CodingColumnId[]> = {
  gl: ['glCode'],
  project: ['project', 'subJob', 'costCode', 'costType'],
  equipment: ['equipment', 'emCostCode', 'emCostType'],
};

/** SEE-974 — inverse of CODING_COLUMNS_BY_TYPE: the transaction type a coding
 *  column belongs to. Columns not present here (vendor, location, department,
 *  employee, subaccount, accountingDate) are common to every type. Derived from
 *  CODING_COLUMNS_BY_TYPE so the two can't drift. */
export const CODING_TYPE_BY_COLUMN: Partial<Record<CodingColumnId, CodingType>> = Object.fromEntries(
  (Object.keys(CODING_COLUMNS_BY_TYPE) as CodingType[]).flatMap((type) =>
    CODING_COLUMNS_BY_TYPE[type].map((colId) => [colId, type])
  )
) as Partial<Record<CodingColumnId, CodingType>>;

/** SEE-974 — display order and header labels for the "Transaction type" coding
 *  groups, shared by the Customize Columns sheet and the export column picker so
 *  the two can't drift in order or wording (they did once). */
export const CODING_GROUP_ORDER: CodingType[] = ['gl', 'project', 'equipment'];
export const CODING_GROUP_LABELS: Record<CodingType, string> = {
  gl: 'Transaction type - GL',
  project: 'Transaction type - Project',
  equipment: 'Transaction type - Equipment',
};

/** Every coding column that is tied to a specific transaction type. A column in
 *  this set is hidden by the type filter unless it belongs to the active type;
 *  columns NOT in it (vendor, transactionType, base columns, ERP dimensions like
 *  location/department) are always kept. */
const TYPE_SCOPED_CODING_IDS: readonly CodingColumnId[] = [
  ...CODING_COLUMNS_BY_TYPE.gl,
  ...CODING_COLUMNS_BY_TYPE.project,
  ...CODING_COLUMNS_BY_TYPE.equipment,
];

/**
 * SEE-974 — when the action-bar "Transaction type" filter is set to GL / Project
 * / Equipment, drop the OTHER types' coding columns from the table so only the
 * relevant codes (plus vendor, type, and base columns) show. `codingType` is the
 * filter value (case-insensitive); 'ALL'/empty/unknown returns the ids unchanged.
 */
export function filterColumnIdsByCodingType<T extends string>(ids: T[], codingType?: string | null): T[] {
  const key = (codingType ?? '').toLowerCase();
  if (key !== 'gl' && key !== 'project' && key !== 'equipment') return ids;
  const relevant = new Set<string>(CODING_COLUMNS_BY_TYPE[key as CodingType]);
  return ids.filter((id) => !TYPE_SCOPED_CODING_IDS.includes(id as CodingColumnId) || relevant.has(id));
}

/**
 * Base (non-coding) column IDs in the transactions table.
 * Locked columns (select, card, merchantName, amount, date) cannot be hidden or reordered.
 * Configurable columns (cardholder, status, receipt, note) can be shown/hidden by the user.
 */
export type BaseColumnId =
  | 'select'
  | 'card'
  | 'cardholder'
  | 'merchantName'
  | 'amount'
  | 'date'
  | 'status'
  | 'receipt'
  | 'note';

/** Union of all column IDs in the transactions table */
export type TransactionColumnId = BaseColumnId | CodingColumnId;

export interface BaseColumnDefinition {
  id: BaseColumnId;
  label: string;
  /** If false, the user cannot hide or reorder this column */
  isUserConfigurable: boolean;
}

/** Base column definitions with configurability flags */
export const BASE_COLUMN_DEFINITIONS: BaseColumnDefinition[] = [
  { id: 'select', label: 'Select', isUserConfigurable: false },
  { id: 'card', label: 'Card', isUserConfigurable: false },
  { id: 'cardholder', label: 'Cardholder', isUserConfigurable: true },
  { id: 'merchantName', label: 'Merchant', isUserConfigurable: false },
  { id: 'amount', label: 'Amount', isUserConfigurable: false },
  { id: 'date', label: 'Date', isUserConfigurable: false },
  { id: 'status', label: 'Status', isUserConfigurable: true },
  { id: 'receipt', label: 'Receipt', isUserConfigurable: true },
  { id: 'note', label: 'Note', isUserConfigurable: true },
];

export type CodingSystemType = 'agave' | 'flex' | 'jobCode';

type CodingSystemPaidol = Pick<
  Paidol,
  'isAgaveClient' | 'erpSpeedchain' | 'erpConnectionId' | 'flexCodingSettings' | 'jobCodeSettings'
>;

/** The coding system a paidol codes in, or null when it has none. One decision for the
 *  coding columns and the submit rule (SEE-1842), so the two can't disagree. */
export function getCodingSystemType(paidol: CodingSystemPaidol | null | undefined): CodingSystemType | null {
  if (hasErpCodingUI(paidol)) return 'agave';
  if (paidol?.flexCodingSettings?.isEnabled) return 'flex';
  if (paidol?.jobCodeSettings?.isEnabled) return 'jobCode';
  return null;
}

/**
 * Agave source system slugs.
 * These must match the values from agaveConstants.ts / AgaveSourceSystem
 */
export type AgaveSourceSystem =
  | 'sage-300-cre'
  | 'sage-intacct'
  | 'quick-books-online'
  | 'quick-books-desktop'
  | 'vista-hosted'
  | 'acumatica'
  | 'cmic'
  | 'sage-100-contractor'
  | 'spectrum';

export interface ColumnDefinition {
  id: CodingColumnId;
  label: string;
  /**
   * Which ERP types support this column.
   * - true: All instances of this ERP type support the column
   * - false: This ERP type does not support the column
   * - string[]: Only these specific ERP source systems support it
   */
  erpAvailability: {
    agave: boolean | AgaveSourceSystem[];
    flex: boolean;
  };
  /**
   * Optional function to check if paidol settings allow this column.
   * Returns true if the column should be available, false otherwise.
   */
  requiresPaidolSetting?: (paidol: Paidol) => boolean;
}

// ─────────────────────────────────────────────────────────────────────
// COLUMN DEFINITIONS (Single source of truth for column metadata)
// ─────────────────────────────────────────────────────────────────────

export const COLUMN_DEFINITIONS: ColumnDefinition[] = [
  // SEE-974 — leads the coding columns (per the Figma). Vista-only; gated behind
  // the equipment flag in useCodingSystemConfig.
  {
    id: 'transactionType',
    label: 'Transaction type',
    erpAvailability: {
      agave: [AgaveSourceSystemConst.VIEWPOINT_VISTA_HOSTED] as AgaveSourceSystem[],
      flex: false,
    },
  },
  {
    id: 'glCode',
    label: 'GL Code',
    erpAvailability: { agave: true, flex: true },
  },
  {
    id: 'project',
    label: 'Project',
    erpAvailability: { agave: true, flex: true },
  },
  // SEE-1362 — subJob sits directly after project by default (its natural spot
  // in the project → sub job → cost code hierarchy); array position drives the
  // default order for columns with no saved `order`.
  {
    id: 'subJob',
    label: 'Sub Job',
    erpAvailability: {
      agave: [
        AgaveSourceSystemConst.SAGE_300_CRE,
        AgaveSourceSystemConst.ACUMATICA,
        AgaveSourceSystemConst.QUICKBOOKS_DESKTOP,
      ] as AgaveSourceSystem[],
      flex: false,
    },
  },
  {
    id: 'costCode',
    label: 'Cost Code',
    erpAvailability: { agave: true, flex: true },
  },
  {
    id: 'costType',
    label: 'Cost Type',
    erpAvailability: { agave: true, flex: true },
  },
  // SEE-974 — Vista equipment coding fields (EM line). Vista-only; gated behind
  // the equipment flag in useCodingSystemConfig.
  {
    id: 'equipment',
    label: 'Equipment',
    erpAvailability: {
      agave: [AgaveSourceSystemConst.VIEWPOINT_VISTA_HOSTED] as AgaveSourceSystem[],
      flex: false,
    },
  },
  {
    id: 'emCostCode',
    label: 'Equipment Cost Code',
    erpAvailability: {
      agave: [AgaveSourceSystemConst.VIEWPOINT_VISTA_HOSTED] as AgaveSourceSystem[],
      flex: false,
    },
  },
  {
    id: 'emCostType',
    label: 'Equipment Cost Type',
    erpAvailability: {
      agave: [AgaveSourceSystemConst.VIEWPOINT_VISTA_HOSTED] as AgaveSourceSystem[],
      flex: false,
    },
  },
  {
    id: 'vendor',
    label: 'Vendor',
    erpAvailability: { agave: true, flex: true },
  },
  {
    id: 'location',
    label: 'Location',
    erpAvailability: { agave: [AgaveSourceSystemConst.SAGE_INTACCT] as AgaveSourceSystem[], flex: false },
    requiresPaidolSetting: (paidol) =>
      Boolean(
        paidol.erpDepartmentLocationSetting?.isEnabled && paidol.erpDepartmentLocationSetting?.locationEnabled
      ),
  },
  {
    id: 'department',
    label: 'Department',
    erpAvailability: {
      agave: [AgaveSourceSystemConst.SAGE_INTACCT, AgaveSourceSystemConst.ACUMATICA] as AgaveSourceSystem[],
      flex: false,
    },
    requiresPaidolSetting: (paidol) => {
      if (paidol.agaveSourceSystemSlug === AgaveSourceSystemConst.ACUMATICA) return true;
      return Boolean(
        paidol.erpDepartmentLocationSetting?.isEnabled &&
          paidol.erpDepartmentLocationSetting?.departmentEnabled
      );
    },
  },
  {
    id: 'subaccount',
    label: 'Subaccount',
    erpAvailability: { agave: [AgaveSourceSystemConst.ACUMATICA] as AgaveSourceSystem[], flex: false },
  },
  {
    id: 'employee',
    label: 'Employee',
    erpAvailability: {
      agave: [AgaveSourceSystemConst.SAGE_INTACCT] as AgaveSourceSystem[],
      flex: false,
    },
  },
  // SEE-1135 — per-transaction billable item. Selecting an item is the billable
  // signal; the post gate marks the CC line billable when the paidol is opted in
  // and the coded project resolves to a customer. Sage-Intacct-only and shown
  // only when the paidol has opted into billable posting.
  {
    id: 'billableItem',
    label: 'Item',
    erpAvailability: { agave: [AgaveSourceSystemConst.SAGE_INTACCT] as AgaveSourceSystem[], flex: false },
    // SEE-1135 + SEE-1112: billable is opt-in AND not applicable to dimension-only
    // paidols (their Item dimension owns the itemid slot, so billable can't ride the
    // line — posting force-ignores it server-side). Hide the column when dimension-only
    // is on, matching the settings toggle's mutual exclusion, so a stale-enabled
    // billable flag doesn't surface a column that has no posting effect.
    requiresPaidolSetting: (paidol) =>
      Boolean(paidol.sageIntacctBillableSettings?.isEnabled) && !paidol.intacctProjectsNotLicensed,
  },
  {
    id: 'jobCode',
    label: 'Job Code',
    erpAvailability: { agave: false, flex: false },
  },
  {
    id: 'accountingDate',
    label: 'Accounting Date',
    erpAvailability: {
      agave: [
        AgaveSourceSystemConst.SAGE_300_CRE,
        AgaveSourceSystemConst.SAGE_INTACCT,
        // SEE-1686 — Vista posts it as the batch month (Mth); the day is dropped.
        AgaveSourceSystemConst.VIEWPOINT_VISTA_HOSTED,
      ] as AgaveSourceSystem[],
      flex: false,
    },
    requiresPaidolSetting: (paidol) => Boolean(paidol.accountingDateSettings?.isEnabled),
  },
];

/**
 * Get a column definition by ID
 */
export function getColumnDefinition(columnId: CodingColumnId): ColumnDefinition | undefined {
  return COLUMN_DEFINITIONS.find((col) => col.id === columnId);
}

// ─────────────────────────────────────────────────────────────────────
// ROLE HIERARCHY
// ─────────────────────────────────────────────────────────────────────

/**
 * Role hierarchy levels - higher number = more access
 */
const ROLE_LEVEL: Record<string, number> = {
  [Role.ADMINISTRATOR]: 3,
  // SEE-946: Secondary and Approval are admin-tier roles for column
  // visibility purposes — they should see everything an Administrator sees
  // on whichever pages they reach (cards/transactions respectively).
  // Without these entries the fallthrough default is 0 (MEMBER level),
  // which would hide admin-only columns from them.
  [Role.SECONDARY_ADMINISTRATOR]: 3,
  [Role.APPROVAL_ADMINISTRATOR]: 3,
  [Role.GROUP_ADMINISTRATOR]: 2,
  [Role.CARDHOLDER]: 1,
  [Role.MEMBER]: 0,
};

/**
 * Map visibility level to required role level
 */
const VISIBILITY_LEVEL: Record<string, number> = {
  [ColumnVisibilityLevel.DISABLED]: Infinity, // No one can see
  [ColumnVisibilityLevel.ADMINISTRATOR]: 3,
  [ColumnVisibilityLevel.GROUP_ADMINISTRATOR]: 2,
  [ColumnVisibilityLevel.CARDHOLDER]: 1,
};

// ─────────────────────────────────────────────────────────────────────
// VISIBILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────

/**
 * Check if a user's role can see a column with the given visibility setting.
 *
 * @param visibility - The visibility level set by admin (from codingColumnsConfig)
 * @param userRole - The current user's role
 * @returns true if the user can see the column
 */
export function canRoleSeeColumn(
  visibility: ColumnVisibilityLevel | null | undefined,
  userRole: Role | null | undefined
): boolean {
  // Default visibility is CARDHOLDER (everyone can see)
  const effectiveVisibility = visibility ?? ColumnVisibilityLevel.CARDHOLDER;

  // DISABLED means no one can see
  if (effectiveVisibility === ColumnVisibilityLevel.DISABLED) {
    return false;
  }

  // Compare role levels
  const requiredLevel = VISIBILITY_LEVEL[effectiveVisibility] ?? 1;
  const userLevel = ROLE_LEVEL[userRole ?? Role.MEMBER] ?? 0;

  return userLevel >= requiredLevel;
}

/**
 * Check if a column is available for the given coding system.
 */
export function isColumnAvailableForCodingSystem(
  columnDef: ColumnDefinition,
  codingSystemType: CodingSystemType,
  sourceSystem?: AgaveSourceSystem
): boolean {
  // jobCode doesn't have system-specific columns
  if (codingSystemType === 'jobCode') {
    return false;
  }

  const availability = columnDef.erpAvailability[codingSystemType];

  // Not available for this ERP type
  if (availability === false) {
    return false;
  }

  // Available for all instances of this ERP type
  if (availability === true) {
    return true;
  }

  // Available only for specific source systems
  if (Array.isArray(availability) && sourceSystem) {
    return availability.includes(sourceSystem);
  }

  return false;
}

/**
 * Check if a column's required paidol settings are enabled.
 *
 * @param columnDef - The column definition
 * @param paidol - The paidol object
 * @returns true if settings requirements are met (or no requirements exist)
 */
export function isColumnSettingEnabled(columnDef: ColumnDefinition, paidol: Paidol): boolean {
  if (!columnDef.requiresPaidolSetting) {
    return true;
  }
  return columnDef.requiresPaidolSetting(paidol);
}

/**
 * Get the full settings object for a specific column from the config.
 */
export function getColumnSettings(
  columnId: CodingColumnId,
  config: CodingColumnsConfig | null | undefined
): CodingColumnSettings | null {
  if (!config) return null;
  if (columnId === 'jobCode') return null;
  return (config[columnId as keyof CodingColumnsConfig] as CodingColumnSettings | null | undefined) ?? null;
}

/**
 * Get the visibility setting for a specific column from the config.
 *
 * @param columnId - The column ID
 * @param config - The codingColumnsConfig from paidol
 * @returns The visibility level or undefined (defaults to CARDHOLDER)
 */
export function getColumnVisibility(
  columnId: CodingColumnId,
  config: CodingColumnsConfig | null | undefined
): ColumnVisibilityLevel | undefined {
  const settings = getColumnSettings(columnId, config);
  return settings?.visibility ?? undefined;
}

/**
 * Get the display label for a column, preferring the admin-configured custom label
 * over the default label from COLUMN_DEFINITIONS.
 *
 * @param columnId - The column ID
 * @param config - The codingColumnsConfig from paidol (optional)
 * @returns The custom label if set, otherwise the default label
 */
export function getColumnLabel(columnId: CodingColumnId, config?: CodingColumnsConfig | null): string {
  const definition = getColumnDefinition(columnId);
  const defaultLabel = definition?.label ?? columnId;

  if (!config) return defaultLabel;
  const settings = getColumnSettings(columnId, config);
  // customLabel will exist after codegen; cast to access pre-codegen
  const customLabel = (settings as CodingColumnSettings & { customLabel?: string | null })?.customLabel;
  return customLabel || defaultLabel;
}

// ─────────────────────────────────────────────────────────────────────
// MAIN VISIBILITY FUNCTION
// ─────────────────────────────────────────────────────────────────────

export interface GetVisibleColumnsParams {
  /** The paidol object containing settings and config */
  paidol: Paidol;
  /** The current user's role */
  userRole: Role | null | undefined;
  /** The coding system type: 'agave' or 'flex' */
  codingSystemType: CodingSystemType;
  /** The specific source system (e.g., 'sage-300-cre', 'sage-intacct') */
  sourceSystem?: AgaveSourceSystem;
}

/**
 * Get the list of column IDs that should be visible for the current context.
 *
 * This function filters columns through three levels:
 * 1. ERP availability - Does the ERP system support this column?
 * 2. Paidol settings - Are the required features enabled?
 * 3. Role visibility - Does the user's role have access?
 *
 * @returns Array of column IDs that should be visible
 */
export function getVisibleColumnIds(params: GetVisibleColumnsParams): CodingColumnId[] {
  const { paidol, userRole, codingSystemType, sourceSystem } = params;

  if (codingSystemType === 'jobCode') {
    return ['jobCode'];
  }

  const config = paidol.codingColumnsConfig as CodingColumnsConfig | null | undefined;

  return COLUMN_DEFINITIONS.filter((columnDef) => {
    // Level 1: Check coding system availability
    if (!isColumnAvailableForCodingSystem(columnDef, codingSystemType, sourceSystem)) {
      return false;
    }

    // Level 2: Check paidol settings requirements
    if (!isColumnSettingEnabled(columnDef, paidol)) {
      return false;
    }

    // Level 3: Check role-based visibility
    const visibility = getColumnVisibility(columnDef.id, config);
    if (!canRoleSeeColumn(visibility, userRole)) {
      return false;
    }

    return true;
  }).map((col) => col.id);
}

/**
 * Get the list of visible column IDs sorted by admin-configured order.
 *
 * Wraps getVisibleColumnIds() and sorts the result by codingColumnsConfig[col].order.
 * Columns without an order value fall back to their position in COLUMN_DEFINITIONS.
 *
 * @returns Array of visible column IDs in admin-configured order
 */
export function getOrderedVisibleColumnIds(params: GetVisibleColumnsParams): CodingColumnId[] {
  const visibleIds = getVisibleColumnIds(params);

  if (params.codingSystemType === 'jobCode') {
    return visibleIds;
  }

  const config = params.paidol.codingColumnsConfig as CodingColumnsConfig | null | undefined;

  return [...visibleIds].sort((a, b) => {
    const settingsA = getColumnSettings(a, config);
    const settingsB = getColumnSettings(b, config);
    const orderA = settingsA?.order ?? Infinity;
    const orderB = settingsB?.order ?? Infinity;
    if (orderA !== orderB) return orderA - orderB;
    // Stable fallback: preserve COLUMN_DEFINITIONS array order
    const indexA = COLUMN_DEFINITIONS.findIndex((col) => col.id === a);
    const indexB = COLUMN_DEFINITIONS.findIndex((col) => col.id === b);
    return indexA - indexB;
  });
}

/**
 * Check if a specific column should be visible for the current context.
 * Convenience function for checking a single column.
 *
 * @returns true if the column should be visible
 */
export function isColumnVisible(columnId: CodingColumnId, params: GetVisibleColumnsParams): boolean {
  const { codingSystemType } = params;

  if (columnId === 'jobCode' && codingSystemType === 'jobCode') {
    return true;
  }

  const columnDef = getColumnDefinition(columnId);
  if (!columnDef) return false;

  const { paidol, userRole, sourceSystem } = params;
  const config = paidol.codingColumnsConfig as CodingColumnsConfig | null | undefined;

  // Level 1: Check coding system availability
  if (!isColumnAvailableForCodingSystem(columnDef, codingSystemType, sourceSystem)) {
    return false;
  }

  // Level 2: Check paidol settings requirements
  if (!isColumnSettingEnabled(columnDef, paidol)) {
    return false;
  }

  // Level 3: Check role-based visibility
  const visibility = getColumnVisibility(columnDef.id, config);
  return canRoleSeeColumn(visibility, userRole);
}

// ─────────────────────────────────────────────────────────────────────
// USER PREFERENCE TYPES + RECONCILIATION
// ─────────────────────────────────────────────────────────────────────

/**
 * A single saved column preference entry, as stored in PaidolUser.columnPreferences (per paidol).columns.
 * Covers both base columns (hide/show only) and coding columns (hide/show + order).
 */
export interface ColumnPreferenceEntry {
  columnId: TransactionColumnId;
  visible: boolean;
  /** Only meaningful for coding columns — position within the coding column slot */
  order: number;
}

/**
 * Configurable base column IDs — shown in the user preferences sidesheet.
 * Locked columns (select, card, merchantName, amount, date) are NOT included here.
 */
export const CONFIGURABLE_BASE_IDS: BaseColumnId[] = ['cardholder', 'status', 'receipt', 'note'];

/**
 * Default order for configurable columns when no user preferences are saved.
 * Coding columns are inserted between cardholder and status.
 */
export function getDefaultConfigurableOrder(
  adminAvailableCodingIds: CodingColumnId[]
): TransactionColumnId[] {
  return ['cardholder', ...adminAvailableCodingIds, 'status', 'receipt', 'note'];
}

export interface ReconciledPreferences {
  /**
   * All user-visible configurable columns in user-preferred order.
   * Locked columns (select, card, merchantName, amount, date) are NOT included.
   * Used to drive the full table column order for the configurable slot.
   */
  orderedVisibleIds: TransactionColumnId[];
  /**
   * Coding column IDs that the user has saved preferences for but which are currently
   * admin-disabled. Shown in the sidesheet as greyed/non-interactive.
   */
  adminDisabledCodingIds: CodingColumnId[];
}

/**
 * Reconcile saved user column preferences against the current admin-available coding columns.
 *
 * All configurable columns (base + coding) are treated as a single unified ordered list.
 * Locked columns (select, card, merchantName, amount, date) are not part of this list.
 *
 * Rules:
 * - Coding columns admin has disabled → moved to adminDisabledCodingIds
 * - Newly admin-enabled coding columns → appended to end, visible by default
 * - Base configurable columns not in saved prefs → visible by default, keep natural position
 * - If no preferences saved → default order used (all visible, admin coding order)
 * - Pinned columns (transactionType) are non-movable/non-hideable: forced to their
 *   canonical position (first coding column, right after cardholder) and always
 *   visible, regardless of saved prefs (SEE-974). They aren't user-customizable, so
 *   prefs that omit or mis-order them must not move them.
 */
export function reconcilePreferences(
  saved: ColumnPreferenceEntry[],
  adminAvailableCodingIds: CodingColumnId[]
): ReconciledPreferences {
  const adminAvailableSet = new Set(adminAvailableCodingIds);
  const savedMap = new Map(saved.map((p) => [p.columnId, p]));

  const codingColumnIdSet = new Set(COLUMN_DEFINITIONS.map((d) => d.id));

  // Coding columns that were saved but are now admin-disabled
  const adminDisabledCodingIds = saved
    .filter(
      (p) =>
        codingColumnIdSet.has(p.columnId as CodingColumnId) &&
        !adminAvailableSet.has(p.columnId as CodingColumnId)
    )
    .map((p) => p.columnId as CodingColumnId);

  const isPinned = (id: TransactionColumnId) => PINNED_FIRST_CODING_COLUMN_IDS.includes(id as CodingColumnId);

  // Force pinned columns (in default order) to the HEAD OF THE CODING SECTION,
  // and always visible. SEE-1141: anchor to the first coding column, NOT to
  // cardholder — cardholder is user-movable, so anchoring at `cardholder + 1`
  // made transactionType tag along whenever cardholder was reordered. Anchoring
  // to the coding-section boundary keeps transactionType leading the coding
  // columns no matter where cardholder is moved among the base columns. Falls
  // back to the end when no other coding columns are available (still visible).
  const pinnedAvailable = adminAvailableCodingIds.filter(isPinned);
  const placePinned = (ids: TransactionColumnId[]): TransactionColumnId[] => {
    if (!pinnedAvailable.length) return ids;
    const rest = ids.filter((id) => !isPinned(id));
    const firstCodingIdx = rest.findIndex((id) => codingColumnIdSet.has(id as CodingColumnId));
    rest.splice(firstCodingIdx >= 0 ? firstCodingIdx : rest.length, 0, ...pinnedAvailable);
    return rest;
  };

  // Full set of currently-available configurable column IDs (card is locked, not included)
  const allAvailableIds = getDefaultConfigurableOrder(adminAvailableCodingIds);

  if (saved.length === 0) {
    return { orderedVisibleIds: placePinned(allAvailableIds), adminDisabledCodingIds: [] };
  }

  // For each available column, look up saved order/visibility, defaulting to visible + end
  const withOrder = allAvailableIds.map((id) => {
    const pref = savedMap.get(id);
    return {
      id,
      visible: pref ? pref.visible : true,
      order: pref ? pref.order : Infinity,
    };
  });

  // Sort by saved order (unsaved columns fall to end in their natural sequence)
  withOrder.sort((a, b) => a.order - b.order);

  const orderedVisibleIds = placePinned(withOrder.filter((c) => c.visible).map((c) => c.id));

  return { orderedVisibleIds, adminDisabledCodingIds };
}

// ─────────────────────────────────────────────────────────────────────
// REQUIRED FIELD UTILITIES
// ─────────────────────────────────────────────────────────────────────

/**
 * Check if a column is marked as required in the admin config.
 */
export function isColumnRequired(
  columnId: CodingColumnId,
  config: CodingColumnsConfig | null | undefined
): boolean {
  const settings = getColumnSettings(columnId, config);
  return (settings as CodingColumnSettings & { isRequired?: boolean })?.isRequired === true;
}

/**
 * Map of coding column IDs to their transaction field names.
 * Used to check if a required field has a value on a transaction.
 */
export const AGAVE_FIELD_MAP: Partial<Record<CodingColumnId, string>> = {
  glCode: 'agaveGlCode',
  project: 'agaveProject',
  costCode: 'agaveCostCode',
  costType: 'agaveCostType',
  vendor: 'agaveVendor',
  subJob: 'agaveSubJob',
  location: 'agaveLocation',
  department: 'agaveDepartment',
  employee: 'agaveEmployee',
  subaccount: 'agaveSubaccount',
  accountingDate: 'accountingDate',
  // SEE-974 — equipment coding fields. `transactionType` is intentionally
  // omitted: it's a derived selector, not a stored field.
  equipment: 'agaveEquipment',
  emCostCode: 'agaveEmCostCode',
  emCostType: 'agaveEmCostType',
};

export const FLEX_FIELD_MAP: Partial<Record<CodingColumnId, string>> = {
  glCode: 'flexGlCode',
  project: 'flexCode',
  costCode: 'flexCostCode',
  costType: 'flexCostType',
  vendor: 'flexVendor',
};

/**
 * Get a list of required coding fields that are missing values on a transaction.
 * Returns column labels for display in error messages.
 *
 * @param transaction - The transaction object to check
 * @param paidol - The paidol with codingColumnsConfig
 * @param codingSystemType - 'agave' or 'flex'
 * @returns Array of missing required field labels (empty if all satisfied)
 */
export function getMissingRequiredFields(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transaction: Record<string, any> | null | undefined,
  paidol: Paidol,
  codingSystemType: CodingSystemType
): string[] {
  if (!transaction || codingSystemType === 'jobCode') return [];

  const config = paidol.codingColumnsConfig as CodingColumnsConfig | null | undefined;
  if (!config || config.isEnabled === false) return [];

  const fieldMap = codingSystemType === 'agave' ? AGAVE_FIELD_MAP : FLEX_FIELD_MAP;
  const missing: string[] = [];

  // SEE-974: a required field belonging to a type OTHER than the row's coding type
  // is greyed out and unfillable, so don't report it as missing. Use the SAME
  // resolver the UI uses (resolveDisplayCodingType — the user's explicit/persisted
  // codingType, never derived from coded ids) so the gate enforces exactly the type
  // shown and greyed on screen. Returns null for an untyped row (or any Flex row),
  // in which case we fall back to enforcing every required field as before.
  const rowCodingType = resolveDisplayCodingType(transaction as CodingTypeFields);

  for (const [columnId, txnField] of Object.entries(fieldMap)) {
    if (!isColumnRequired(columnId as CodingColumnId, config)) continue;
    if (!txnField) continue;

    const columnType = CODING_TYPE_BY_COLUMN[columnId as CodingColumnId];
    if (rowCodingType && columnType && columnType !== rowCodingType) continue;

    const value = transaction[txnField];
    if (!value) {
      missing.push(getColumnLabel(columnId as CodingColumnId, config));
    }
  }

  // SEE-974: transactionType isn't a stored field (not in AGAVE_FIELD_MAP) — its
  // value is the RESOLVED type (persisted codingType, else derived from coding).
  // When the admin marks it required, block posting if the row resolves to no
  // type (a GL/project/equipment-coded row resolves to one, so it passes; only a
  // completely-untyped row fails). Agave-only — Flex has no transaction type.
  if (codingSystemType === 'agave' && isColumnRequired('transactionType', config)) {
    if (!resolveDisplayCodingType(transaction as CodingTypeFields)) {
      missing.push(getColumnLabel('transactionType', config));
    }
  }

  return missing;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const noop = (..._args: unknown[]): void => {};

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const asyncNoop = async (..._args: unknown[]): Promise<void> => {};
